import React from 'react';
import TableWindow from '../../../../components/Table/TableWindow';
import availableColumns from '../waterSites/WaterSites.columns';
import useTablePages from '../../../../hooks/useTablePages';
import { useOrderBy } from '../../../../hooks/useOrderBy';
import PageBar from '../../../../components/PageBar/PageBar';
import {
  GET_WATER_SITE_IDS,
  GET_WATER_SITES,
  WaterSiteItem,
} from '../waterSites/WaterSites.query';
import { RouteComponentProps, withRouter } from 'react-router';
import ThirdPaneLayout, {
  MainColumn,
} from '../../../../components/Layout/ThirdPaneLayout';
import useThirdPaneCallbacks from '../../../../hooks/useThirdPaneCallbacks';
import FilterBarRecycledSites, {
  defaultFilters,
  RecycledSiteFilters,
} from './FilterBarRecycledSites';
import useMode from '../../../../hooks/useMode';
import useColumnConfiguration from '../../../../hooks/useColumnConfiguration';
import useStoredFilters from '../../../../hooks/useStoredFilters';
import WaterSitesActions from '../waterSites/WaterSitesActions';
import WaterSiteThirdPane from '../../../../components/WaterSite/WaterSiteThirdPane';
import { PortalWithState } from 'react-portal';
import Button from '../../../../components/Button/Button';
import WaterSiteEditModal from '../../../../components/WaterSite/WaterSiteEditModal';
import { getStringSearchOr } from '../../../../helpers/search';
import RequirePerms from '@/components/RequirePerms';

type RecycledSiteDetailParams = {
  id?: string;
};

type RecycledSiteDetailProps = RouteComponentProps<RecycledSiteDetailParams>;

export const searchFields = [
  {
    field: 'name',
    label: 'Name',
  },
  {
    field: 'supervisor.name',
    label: 'Supervisor',
  },
  {
    field: 'assignedTo.name',
    label: 'Assignee',
  },
  {
    field: 'address.street1',
    label: 'Address',
  },
  {
    field: 'address.street2',
    label: null,
  },
];

const convertStringSearch = (stringSearch?: string) => ({
  or: getStringSearchOr(searchFields, stringSearch),
});

const convertFilters = (storedFilters: RecycledSiteFilters) => {
  return {
    type: 'RECYCLED',
    ...convertStringSearch(storedFilters.stringSearch),
  };
};

/**
 * @component for recycled sites page
 */
function RecycledSites({ match }: RecycledSiteDetailProps): JSX.Element {
  const { storedFilters, setStoredFilters, queryFilters } =
    useStoredFilters<RecycledSiteFilters>(
      convertFilters,
      'recycledSites',
      defaultFilters
    );

  const { orderBy, onHeaderClick } = useOrderBy(
    'recycledSites',
    availableColumns
  );

  const {
    loading,
    error,
    itemIds,
    selectedIds,
    setSelectedIds,
    itemLoader,
    refetch,
  } = useTablePages<WaterSiteItem>(
    'waterSites',
    GET_WATER_SITE_IDS,
    {
      where: queryFilters,
      limit: null,
      orderBy,
    },
    GET_WATER_SITES,
    (ids) => ({
      where: {
        id: ids,
      },
      limit: null,
    })
  );

  const { visibleColumns, columnConfiguration, setColumnConfiguration } =
    useColumnConfiguration(
      availableColumns,
      'storedColumnConfigurationRecycledSites'
    );

  const { closeThirdPane, onRowClick, prev, prevEnabled, next, nextEnabled } =
    useThirdPaneCallbacks(
      `/${useMode()}/recycled-sites`,
      itemIds,
      match.params.id
    );

  return (
    <ThirdPaneLayout paneVisible={!!match.params.id}>
      <MainColumn px="mainContent" pb="mainContent">
        <PageBar title="Recycled Sites">
          <RequirePerms waterSites="CREATE">
            <PortalWithState closeOnEsc>
              {({ openPortal, closePortal, portal }) => (
                <>
                  <Button
                    ml={3}
                    variant="primary"
                    icon="plus"
                    onClick={openPortal}
                  >
                    Add Site
                  </Button>
                  {portal(
                    <WaterSiteEditModal
                      action="add"
                      onClose={() => {
                        refetch();
                        closePortal();
                      }}
                    />
                  )}
                </>
              )}
            </PortalWithState>
          </RequirePerms>
        </PageBar>
        <FilterBarRecycledSites
          filters={storedFilters}
          setFilters={setStoredFilters}
          selectedId={match.params.id}
        />
        {loading && itemIds.length === 0 ? (
          'Loading...'
        ) : (
          <TableWindow
            emptyMessage="No recycled sites found."
            columns={visibleColumns}
            itemIds={itemIds}
            itemLoader={itemLoader}
            onSelectionChange={setSelectedIds}
            selectedIds={selectedIds}
            actionItems={<WaterSitesActions ids={selectedIds} />}
            activeIds={match.params.id ? [match.params.id] : undefined}
            onHeaderClick={onHeaderClick}
            orderBy={orderBy}
            onRowClick={onRowClick}
            columnConfiguration={columnConfiguration}
            setColumnConfiguration={setColumnConfiguration}
          />
        )}
      </MainColumn>
      <WaterSiteThirdPane
        waterSiteId={match.params.id}
        onPrev={prev}
        prevEnabled={prevEnabled}
        onNext={next}
        nextEnabled={nextEnabled}
        onToggle={closeThirdPane}
      />
    </ThirdPaneLayout>
  );
}

export default withRouter(RecycledSites);
